import { connect } from 'react-redux';
import { gameConfig } from '../../gameConfig';
import ErrorContainer from '../scripts/error-container/errorContainer';
import { asset } from '../services/assetResolverService';
import { AdblockModalContainer } from './adblock/AdblockModalContainer';
import { AditudeCpmstarSkin } from './ads/aditudeCpmstarSkin';
import { AditudeOutstream } from './ads/aditudeOutstream';
import { ConnatixOutstream } from './ads/connatixOutstream';
import { AdhesionPlacement } from './ads/adhesionPlacement';
import { ExCoRichMedia } from './ads/excoVideo';
import { InStreamStateToggler } from './ads/instreamVideoContext';
import { PlaywireForcePath } from './ads/playwireForcePath';
import { PrimisOutstream } from './ads/primisOutstream';
import { LEGACY_LEFT_RAIL_MIN_VIEWPORT_WIDTH } from './ads/adViewportThresholds';
import VenatusRichMedia from './ads/venatusRichMedia';
import { VideoDesktopComponent, VideoDockedComponent, VideoMobileComponent } from './ads/videoComponent';
import { Zone, ZoneContainer } from './ads/zone';
import { AllowBodyScrolling } from './allowBodyScrolling';
import { DelayedMount } from './delayedMount';
import { ErrorBoundary } from './errorBoundary';
import { GlobalReportModal } from './moderation/GlobalReportModal';
import { LocationMetaTags } from './locationMetaTags';
import { Media } from './presentational/media';
import { ReamazeWidget } from './reamazeWidget';
import { SiteBackgroundOverride } from './siteBackgroundOverride';
import { ClarityLoader } from './clarityLoader';
import { WowheadLoader } from './wowheadLoader';
import { FC, useEffect } from 'react';
import { Link, TrackedLink } from './link';
import { useMedia } from './grid/hooks';
import { Outlet } from 'react-router-dom';

type TAdhesionZoneType = 'bottom_rail' | 'mobile_footer';

const renderAdhesionZonePlacement = (type: TAdhesionZoneType, maxWidth: number, height: number) => (
  <AdhesionPlacement height={height} maxWidth={maxWidth}>
    <Zone type={type} destroyOnUnmount={true} />
  </AdhesionPlacement>
);

export const AppBaseComponent: FC<any> = (props: any) => {
  const renderedChildren = props.children || <Outlet />;

  if (props.isEmbedded) {
    return (
      <>
        <Media lessThan="lg">
          <AllowBodyScrolling />
        </Media>
        {renderedChildren}
      </>
    );
  }

  // isEmbedded never changes, so we can have these hooks AFTER that
  const isBigScreen = useMedia([`(min-width: ${LEGACY_LEFT_RAIL_MIN_VIEWPORT_WIDTH}px)`], [true], false);

  useEffect(() => {
    if (!isBigScreen) {
      if (props.displayAdsProvider === 'nitropay') {
        // screen has gotten too small for left rail so just remove it
        document.getElementById('left_rail')?.remove();
      }
    }
  }, [isBigScreen]);

  return (
    <>
      <ClarityLoader />
      <WowheadLoader />
      <LocationMetaTags />

      {!props.noAds && props.videoAdsProvider && (
        <>
          {['venatus'].includes(props.videoAdsProvider) && (
            <Media greaterThan="sm">
              <VideoDesktopComponent {...props} />
            </Media>
          )}

          {!['venatus', 'playwire', 'exco', 'tds', 'aditude', 'connatix', 'primis'].includes(
            props.videoAdsProvider
          ) && (
            <Media at="sm">
              <VideoMobileComponent />
            </Media>
          )}
        </>
      )}

      <props.authBarComponent {...props} />

      <div
        className="slds-container--x-large slds-container--center rio-main-content--border"
        id="main-content-container"
      >
        <props.navBarComponent {...props} />

        {props.videoAdsProvider === 'playwire' && (
          <>
            <Zone type="playwire_video" hidden={true} destroyOnUnmount={true} />

            <DelayedMount ms={1}>
              <InStreamStateToggler
                inStreamComponent={<PlaywireForcePath useDefault />}
                outStreamComponent={<PlaywireForcePath forcePath="corner-video" />}
              />
            </DelayedMount>
          </>
        )}

        <ErrorContainer />

        {props.preContentComponents}

        <div className="rio-main-content">
          <ErrorBoundary>
            {!props.noAds && (
              <>
                {props.displayAdsProvider !== 'venatus' && (
                  <Media at="sm">
                    {props.displayAdsProvider !== 'playwire' && (
                      <ZoneContainer
                        className="slds-align--absolute-center slds-m-vertical--x-small"
                        type="mobile_header"
                      />
                    )}

                    {/*
                  We only show the mobile component when you aren't logged in - even if you SHOULD see ads.
                  This is because the mobile component can consume a lot of bandwidth, so we want to give
                  a perk for those that log in on mobile. It will be a smaller percentage of the overall
                  views from mobile, so hopefully shouldn't have any reasonable impact.
                */}
                    {/* {!props.authenticated && props.videoAdsProvider && <VideoMobileComponent />} */}
                  </Media>
                )}
                {props.videoAdsProvider === 'venatus' && (
                  <>
                    {/* Venatus wants to make sure the mobile video component is within content areas */}
                    <Media at="sm">
                      <VideoMobileComponent isPlacedAtRoot={true} />
                    </Media>

                    {props.venatusAdsVersion === 'v1' && (
                      <>
                        <Media greaterThan="sm">
                          <VideoDockedComponent inStreamPlacement={false} />
                        </Media>
                      </>
                    )}
                  </>
                )}
                {props.videoAdsProvider === 'nitropay' && (
                  <>
                    <Media at="sm">
                      <VideoMobileComponent isPlacedAtRoot={true} />
                    </Media>

                    <Media greaterThan="sm">
                      <Zone type="nitro_video_outstream" destroyOnUnmount={true} />
                    </Media>
                  </>
                )}
                {props.videoAdsProvider === 'exco' && (
                  <>
                    <Media at="sm">
                      <VideoMobileComponent isPlacedAtRoot={true} />
                    </Media>
                    <Media greaterThan="sm">
                      <ExCoRichMedia delayBeforeAssessmentMs={1000} />
                    </Media>
                  </>
                )}
                {props.videoAdsProvider === 'aditude' && <AditudeOutstream />}
                {props.videoAdsProvider === 'primis' && <PrimisOutstream />}
                {props.videoAdsProvider === 'connatix' && <ConnatixOutstream config={props.connatixConfig} />}
              </>
            )}
            {renderedChildren}
          </ErrorBoundary>
        </div>

        {props.postContentComponents}
      </div>

      <GlobalReportModal />

      {!props.hideFooter && (
        <div className="slds-container--x-large slds-container--center rio-main-content--border">
          <props.siteFooterComponent {...props} components={{ Link, TrackedLink }} />
        </div>
      )}

      <props.cookieFooterComponent />

      {/* venatus always wants the skin/richmedia tag added */}
      {!props.noAds && props.displayAdsProvider === 'venatus' && (
        <>
          {props.venatusAdsVersion === 'v1' ? (
            <ZoneContainer type="skin" />
          ) : (
            <>
              <Media at="sm">
                <ZoneContainer type="richmedia_outstream" />
              </Media>
              <Media greaterThan="sm">
                <VenatusRichMedia
                  instreamUnitId="64ca7a1dbab7e94502ecfb51"
                  outstreamUnitId="64cb9f5dbab7e94502ecfbae"
                  delayBeforeAssessmentMs={1000}
                />
              </Media>
            </>
          )}
        </>
      )}

      <Media greaterThan="sm">
        {!props.noAds && props.displayAdsProvider === 'playwire' && (
          <>
            <Zone type="playwire_flex_leaderboard" hidden={true} destroyOnUnmount={true} />
            <Zone type="playwire_site_skin" hidden={true} destroyOnUnmount={true} />
          </>
        )}

        <DelayedMount ms={15000}>
          <ReamazeWidget />
        </DelayedMount>
      </Media>

      {/* Note: mediavine injects its own mobile anchor ad dynamically */}
      {!props.noAds && ['nitropay', 'tds'].includes(props.displayAdsProvider) && (
        <Media at="sm">
          <div className="slds-text-align--center">
            <ZoneContainer type="mobile_footer" />
          </div>
        </Media>
      )}

      {!props.noAds && props.displayAdsProvider === 'playwire' && (
        <>
          <Zone type="playwire_in_content" hidden={true} destroyOnUnmount={true} />
        </>
      )}

      {!props.noAds && props.displayAdsProvider === 'aditude' && (
        <>
          <AditudeCpmstarSkin />

          <Media greaterThan="sm">
            {(_mediaClassName: string, renderChildren: boolean) =>
              renderChildren ? renderAdhesionZonePlacement('bottom_rail', 970, 90) : null
            }
          </Media>

          <Media at="sm">
            {(_mediaClassName: string, renderChildren: boolean) =>
              renderChildren ? renderAdhesionZonePlacement('mobile_footer', 320, 100) : null
            }
          </Media>
        </>
      )}

      {/* Note: mediavine injects its own bottom rail ad dynamically */}
      {!props.noAds && ['playwire', 'nitropay'].includes(props.displayAdsProvider) && (
        <Zone
          type="bottom_rail"
          destroyOnUnmount={true}
          {...(props.displayAdsProvider === 'playwire' ? { hidden: true } : {})}
        />
      )}

      {!props.noAds && ['playwire', 'nitropay', 'tds'].includes(props.displayAdsProvider) && (
        <Zone
          type="left_rail"
          destroyOnUnmount={true}
          {...(props.displayAdsProvider === 'playwire' ? { hidden: true } : {})}
        />
      )}

      {!props.noAds && props.videoAdsProvider === 'tds' && (
        <>
          <Media greaterThan="sm">
            <VideoDesktopComponent />
          </Media>

          <Media at="sm">
            <VideoMobileComponent />
          </Media>
        </>
      )}

      {gameConfig.SiteBackdropPath && <SiteBackgroundOverride imageUrl={asset(gameConfig.SiteBackdropPath)} />}

      {!props.noAds && <AdblockModalContainer />}
    </>
  );
};

function mapStateToProps(state: any) {
  return {
    noAds: state.currentFeatures.no_ads,
    authenticated: state.currentFeatures.authenticated,
    isEmbedded: state.siteConfig.isEmbedded,
    videoAdsProvider: state.siteConfig.videoAdsProvider,
    displayAdsProvider: state.siteConfig.displayAdsProvider,
    venatusAdsVersion: state.siteConfig.venatusAdsVersion,
    connatixConfig: state.siteConfig.connatixConfig,
    consentManagementEnabled: state.consentManagementEnabled
  };
}

export const AppBase = connect<any, any, any>(mapStateToProps)(AppBaseComponent);
